// Shared types between frontend and backend

// NOTE: This mirrors the Prisma Status enum from the backend schema.
// The backend uses the Prisma-generated `Status` enum directly.
export enum SessionStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum InterviewMode {
  LIVE_VOICE = 'LIVE_VOICE',
  ASYNC_CHAT = 'ASYNC_CHAT',
}

export interface InterviewSession {
  id: string;

  // Link to admin data
  invitationId: string;
  structureId: string;

  // Denormalized snapshot (from admin webhook)
  intervieweeName: string;
  intervieweeEmail: string;
  structureName: string;
  structureVersion: string;
  structureData: Record<string, unknown>;

  // Interview execution data
  sessionName: string;
  status: SessionStatus;
  mode: InterviewMode;
  language: string;
  coordinatorModel?: string;
  vapiCallId?: string;
  vapiSessionId?: string;
  duration?: number;
  metadata?: Record<string, unknown>;
  startedAt?: Date;
  completedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface VapiCallStatus {
  callId: string;
  status: 'ringing' | 'in-progress' | 'forwarding' | 'ended';
  startedAt?: string;
  endedAt?: string;
  transcript?: string;
}

export interface ApiError {
  error: string;
  details?: unknown;
  statusCode?: number;
}

export interface HealthCheckResponse {
  status: 'ok' | 'error';
  timestamp: string;
  version: string;
  database: 'connected' | 'disconnected';
}

// VAPI-specific types
export interface VapiMessage {
  type: 'transcript' | 'function-call' | 'speech-update' | 'metadata' | 'conversation-update';
  role?: 'user' | 'assistant' | 'system';
  transcript?: string;
  transcriptType?: 'partial' | 'final';
  timestamp?: string;
}

// ============================================================================
// Chat Interview Types (FEAT-9)
// ============================================================================

export interface ChatMessage {
  id: string;
  sessionId: string;
  role: 'INTERVIEWER' | 'INTERVIEWEE';
  content: string;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
}

export interface ChatMessageRequest {
  sessionId: string;
  message: string;
  canvasEvents?: CanvasEvent[];
  canvasContext?: CanvasContextSummary;
}

export interface ChatMessageResponse {
  message: string;
  metadata?: {
    section?: string;
    progress?: number;
    isComplete?: boolean;
    agentSignals?: Record<string, unknown>;
  };
  canvasDelta?: CanvasDelta;
  canvasCommands?: CanvasCommand[];
}

// ============================================================================
// Live Process Canvas Types (FEAT-11)
// ============================================================================

/** A user-added context entry on a canvas node */
export interface NodeContextEntry {
  text: string;
  timestamp: string;
  /** Category from the AddContextSheet — optional for back-compat with older entries. */
  kind?: 'detail' | 'exception' | 'correction';
}

/** Source provenance of a canvas node */
export type CanvasNodeSourceType = 'new' | 'existing' | 'conflicting' | 'ambiguous';

/** Action to apply to a node */
export type CanvasNodeAction = 'add' | 'update' | 'activate' | 'conflict' | 'ambiguous';

/** Action to apply to an edge */
export type CanvasEdgeAction = 'add' | 'update';

/** Canvas display types — the 5 simplified visual categories */
export type CanvasDisplayType = 'STEP' | 'ACTOR' | 'TOOL' | 'INSIGHT' | 'TRIGGER';

/** Legacy canvas node types from FEAT-10 (kept for backward compat with persisted data) */
export type CanvasNodeType = CanvasDisplayType | 'TASK' | 'PERSON' | 'SYSTEM' | 'DOCUMENT' | 'RULE' | 'DECISION' | 'ROLE' | 'EVENT' | 'BUSINESS_OBJECT' | 'KPI' | 'RISK' | 'WORKAROUND';

/** Canvas edge/relationship types (simplified from 12 to 8) */
export type CanvasEdgeType =
  | 'NEXT_STEP'
  | 'USES'
  | 'RESPONSIBLE_FOR'
  | 'GOVERNED_BY'
  | 'TRIGGERS'
  | 'MEASURED_BY'
  | 'MITIGATES'
  | 'RELATES_TO'
  // Legacy types kept for backward compat
  | 'REFERENCES'
  | 'APPROVES'
  | 'FILLS_ROLE'
  | 'PROCESSES'
  | 'WORKS_AROUND';

/** Position on the canvas */
export interface CanvasPosition {
  x: number;
  y: number;
}

/** A node update within a canvas delta */
export interface CanvasNodeUpdate {
  id: string;
  type: CanvasNodeType;
  name: string;
  description?: string;
  action: CanvasNodeAction;
  position?: CanvasPosition;
  sourceType: CanvasNodeSourceType;
  /** Original granular type when type is a display category (e.g., "RISK" when type="INSIGHT") */
  subType?: string;
  /** Interview section this node belongs to (for visual grouping) */
  section?: string;
  /** Step number for STEP nodes */
  stepNumber?: number;
  /** For conflicting nodes: what the new statement says */
  conflictNew?: string;
  /** For conflicting nodes: what the existing knowledge says */
  conflictExisting?: string;
  /** For existing nodes: original interviewee name */
  sourceName?: string;
  /** For existing nodes: original interview date */
  sourceDate?: string;
  /** Whether this node is staged (provisional, pending user confirmation) */
  staged?: boolean;
  /** User-added context notes */
  additionalContext?: NodeContextEntry[];
}

/** An edge update within a canvas delta */
export interface CanvasEdgeUpdate {
  id: string;
  type: CanvasEdgeType;
  sourceId: string;
  targetId: string;
  action: CanvasEdgeAction;
}

/** Delta payload returned by the canvas extraction agent */
export interface CanvasDelta {
  nodes: CanvasNodeUpdate[];
  edges: CanvasEdgeUpdate[];
}

// ============================================================================
// Canvas Interview Bridge Types
// ============================================================================

/** Canvas → Agent event types */
export type CanvasEventType =
  | 'nodeConfirmed'
  | 'nodeDismissed'
  | 'nodeContextAdded'
  | 'clusterApproved'
  | 'clusterDismissed';

/** A canvas event queued for the next API call */
export interface CanvasEvent {
  id: string;
  timestamp: string;
  type: CanvasEventType;
  payload: {
    nodeId?: string;
    nodeLabel?: string;
    nodeType?: string;
    contextText?: string;
    clusterName?: string;
    clusterNodeIds?: string[];
  };
}

/** Agent → Canvas command types */
export type CanvasCommandType =
  | 'focusNode'
  | 'focusArea'
  | 'highlightNode'
  | 'proposeClustering'
  | 'clearHighlights';

/** A single command from agent to canvas */
export interface CanvasCommand {
  type: CanvasCommandType;
  payload: {
    nodeId?: string;
    areaId?: string;
    clusters?: Array<{ id: string; name: string; nodeIds: string[] }>;
  };
}

/** A confirmed node as seen by the coordinator */
export interface CanvasNodeSummary {
  id: string;
  type: string;
  name: string;
  description?: string;
  section?: string;
  hasAnnotations: boolean;
  annotationSnippet?: string;
}

/** A dismissed node (DO NOT ask about again) */
export interface DismissedNodeSummary {
  id: string;
  name: string;
  type: string;
  dismissedAt: string;
}

/** A user annotation on a canvas node */
export interface CanvasAnnotationSummary {
  nodeId: string;
  nodeName: string;
  text: string;
  addedAt: string;
}

/** A cluster/knowledge area */
export interface CanvasClusterSummary {
  name: string;
  nodeCount: number;
  nodeTypes: string[];
  /** IDs of every node in this cluster. Used by the canvas-command agent
   *  to detect whether a section's nodes are already grouped (regardless
   *  of whether the user renamed the cluster). Optional for backward
   *  compatibility with payloads from older frontends. */
  nodeIds?: string[];
}

/** Canvas context summary sent with every chat message */
export interface CanvasContextSummary {
  nodeCount: number;
  confirmedCount: number;
  stagedCount: number;
  clusterCount: number;
  recentChanges: string[];
  pendingStagedNodes: Array<{ id: string; name: string; type: string }>;
  confirmedNodes?: CanvasNodeSummary[];
  dismissedNodes?: DismissedNodeSummary[];
  annotations?: CanvasAnnotationSummary[];
  clusters?: CanvasClusterSummary[];
}

/** Persisted canvas node (includes position) */
export interface CanvasNode {
  id: string;
  type: CanvasNodeType;
  name: string;
  description?: string;
  position: CanvasPosition;
  sourceType: CanvasNodeSourceType;
  /** Original granular type */
  subType?: string;
  /** Interview section for grouping */
  section?: string;
  /** Step number for STEP nodes */
  stepNumber?: number;
  /** Number of connected edges */
  edgeCount?: number;
  /** For conflicting nodes */
  conflictNew?: string;
  conflictExisting?: string;
  /** For existing/preloaded nodes */
  sourceName?: string;
  sourceDate?: string;
  /** Whether this node is staged (provisional) */
  staged?: boolean;
  /** User-added context notes */
  additionalContext?: NodeContextEntry[];
}

/** Persisted canvas edge */
export interface CanvasEdge {
  id: string;
  type: CanvasEdgeType;
  sourceId: string;
  targetId: string;
}

/** A cluster grouping nodes into a knowledge area */
export interface CanvasCluster {
  id: string;
  name: string;
  nodeIds: string[];
  color: string;
  bounds: { x: number; y: number; width: number; height: number };
}

/** A node that was explicitly dismissed by the user */
export interface DismissedCanvasNode {
  id: string;
  name: string;
  type: string;
  dismissedAt: string;
}

/** Full persisted canvas state on InterviewSession */
export interface CanvasState {
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  clusters?: CanvasCluster[];
  /** Nodes the user explicitly dismissed (for undo and coordinator awareness) */
  dismissedNodes?: DismissedCanvasNode[];
  lastUpdatedAt: string;
  /** Schema version for future migration */
  version?: number;
}

/** Response from GET /api/interview/chat/canvas/:sessionId */
export interface CanvasContextResponse {
  canvasState: CanvasState | null;
  preloadedEntities: CanvasNodeUpdate[];
}

export interface ChatStartRequest {
  sessionId: string;
  mode?: 'ASYNC_CHAT';
  language?: string;
  coordinatorModel?: string;
}

export interface ChatStartResponse {
  sessionId: string;
  status: SessionStatus;
  message: string;
  metadata?: {
    section?: string;
    progress?: number;
    isComplete?: boolean;
  };
}

export interface ChatHistoryResponse {
  sessionId: string;
  messages: ChatMessage[];
  messageCount: number;
}

// Re-export knowledge types for convenience
export type {
  KnowledgeCategory,
  KnowledgeChunk,
  KnowledgeQueryResult,
  KnowledgeQueryRequest,
  KnowledgeQueryResponse,
  KnowledgeQueryFilters,
  ConversationTurn,
  ChunkSessionMetadata,
} from './knowledge';

// Re-export knowledge graph types (FEAT-10)
export type {
  EntityType,
  RelationType,
  EntityStatus,
  KnowledgeEntity,
  KnowledgeRelation,
  EntitySource,
  KnowledgeEntityDetail,
  RelatedEntity,
  KnowledgeGraphData,
  KnowledgeEntityListResponse,
  KnowledgeEntityFilters,
  KnowledgeEntityQueryResponse,
  KnowledgeGraphFilters,
} from './knowledge-graph';
